import { TZDate } from '@date-fns/tz'
import { format } from 'date-fns/format'
import { isValid } from 'date-fns/isValid'
import { parseISO } from 'date-fns/parseISO'
import { differenceInDays } from 'date-fns/differenceInDays'
import { startOfDay } from 'date-fns/startOfDay'

export const utcIntoZonedTime = (inputDate: InputDate, timezone: string): TZDate | null => {
  const date = getDate(inputDate)
  if (!isValid(date)) return null

  return timezone ? new TZDate(date, timezone) : new TZDate(date)
}

const getDate = (inputDate: InputDate): Date =>
  inputDate instanceof Date ? inputDate : parseISO(inputDate)

export const formatDate = (inputDate: InputDate, formatStr: string): string => {
  const date = getDate(inputDate)
  return isValid(date) ? format(date, formatStr) : ''
}

export const formatExpiration = (input: InputDate, formatStr = 'M/d'): string =>
  `Expires ${formatDate(input, formatStr)}`

/**
 * Normalizes expiration date strings to MM/DD.
 * Supported inputs: YYYY-MM-DD, MM/DD/YYYY, and M/D.
 */
export const formatExpirationMonthDay = (rawExpirationDate?: string) => {
  if (!rawExpirationDate) return undefined

  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(rawExpirationDate)) {
    const formatted = formatDate(rawExpirationDate, 'MM/dd')
    return formatted || rawExpirationDate
  }

  const [month, day] = rawExpirationDate.split('/')
  if (month && day) {
    return `${month.padStart(2, '0')}/${day.padStart(2, '0')}`
  }

  return rawExpirationDate
}

export const getDaysUntilExpire = (expiration: string) => {
  if (!expiration) return 0
  return Math.abs(Math.round(differenceInDays(new Date(expiration), startOfDay(new Date()))))
}

export const getDateTimeInfoWithSeconds = (seconds: number) => ({
  minutes: Math.floor(seconds / 3600),
  hours: Math.floor(seconds / 3600),
  days: Math.floor(seconds / 86400),
  weeks: Math.floor(seconds / 604800),
  years: Math.floor(seconds / 31536000),
})

// types
type InputDate = string | Date

export const getHourMinutePeriodWithTimezone = (dateTimeStr: string, timezone: string): string => {
  const zonedTime = utcIntoZonedTime(dateTimeStr, timezone)
  if (!zonedTime) return ''

  return format(zonedTime, 'h:mm a').toLowerCase()
}

// e.g 2024-01-22T00:00:00Z will return Jan 22 regardless of user's timezone
export const formatDateWithoutTimezone = (date: InputDate) => {
  const dateInUtc = utcIntoZonedTime(date, 'UTC')
  return dateInUtc ? formatDate(dateInUtc, 'MMM d') : ''
}
